// Copyright 2022 Sorama B.V.

import { FrontEnd } from "datatypes/SoramaLocalApi";
import { SorXMetadataFull } from "datatypes/SorXFormat";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { ICamIV64File } from "./CamIV64File";

export interface FirmwareInfo {
    version: string;
    installDate: Date;
}

export interface SpecialMeasurementFileBase {
    name?: string;
    fileName?: string;
}

export interface PackageFileInfo {
    data: string;
    sig: string;
}

export abstract class ICamApi {
    /**
     * Checks if the device exists given the connection info
     */
    abstract canConnect(): Promise<boolean>;

    //#region device

    /**
     * Returns all identification codes of the cam
     */
    abstract getDeviceId(): Promise<FrontEnd>;

    /**
     * Returns the configuration of the cam
     */
    abstract getDeviceConfig(): Promise<unknown>;

    /**
     * Returns firmware version(s) of the firmware currently flashed on this device and state of firmware update process.
     */
    abstract getDeviceFirmware(): Promise<FirmwareInfo>;

    /**
     * Updates the cam with the provided firmware binary.
     * @param firmware binary firmware blob
     * @param progressCallback a callback function for keeping track of firmware upload progress
     * @param force whether the update should start immediately on the device
     */
    abstract updateDeviceFirmware(
        firmware: Blob,
        progressCallback: (progressIndicator: ProgressIndicator) => void,
        force?: boolean
    ): Promise<void>;

    /**
     * Performs a factory reset of the device
     */
    abstract performFactoryReset(): Promise<void>;

    //#endregion

    //#region measurements

    /**
     * Returns a list of file paths of the measurements present on the cam.
     */
    abstract getMeasurementFileStructure(): Promise<ICamIV64File[]>;

    /**
     * Gets a measurement file url for download
     * @param filePath a string of one of the file paths in the cam's file structure
     */
    abstract getMeasurementFileUrl(filePath: string): Promise<string>;

    /**
     * Returns a list of file paths of the special measurements present on the cam.
     */
    abstract getSpecialMeasurementFileStructure(): Promise<ICamIV64File[]>;

    /**
     * Returns a list of files of the CAM iV64 device.
     */
    abstract getCAMiV64Files(): Promise<ICamIV64File[]>;

    /**
     * Gets a special measurement file url for download
     * @param filePath a string of one of the file paths in the cam's file structure
     */
    abstract getSpecialMeasurementFileUrl(filePath: string): Promise<string>;

    /**
     * Returns a special measurement given its file location.
     * @param filePath a string of one of the file paths in the cam's file structure
     * @param progressCallback a callback function for keeping track of download progress
     */
    abstract downloadSpecialMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<SpecialMeasurementFileBase>;

    /**
     * Returns a sorx measurement's metadata (used for report generation) given its file location.
     * @param filePath a string of one of the file paths in the cam's file structure
     * @param progressCallback a callback function for keeping track of download progress
     */
    abstract downloadSorXMeasurementMetadata(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<SorXMetadataFull>;

    /**
     * Returns a sorx measurement given its file location.
     * @param filePath a string of one of the file paths in the cam's file structure
     * @param progressCallback a callback function for keeping track of download progress
     */
    abstract downloadSorXMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<Blob>;

    /**
     * Gets a url for downloading the logs
     * @param filePath a string of one of the file paths in the cam's file structure
     */
    abstract getLogUrl(): Promise<string>;

    /**
     * Returns a measurement given its file location.
     * @param filePath a string of one of the file paths in the cam's file structure
     * @param progressCallback a callback function for keeping track of download progress
     */
    abstract downloadMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<Blob>;

    /**
     * Deletes the the specified folder or file
     * @param filePath a string of one of the file paths or folders in the cam's file structure
     */
    abstract deleteFileOrFolder(filePath: string): Promise<void>;

    /**
     * Gets the cams hardware clocks time
     */
    abstract getHardwareClock(): Promise<number>;

    /**
     * Get the license tags from the device
     */
    abstract getLicenseTagsOnDevice(): Promise<string[]>;

    /**
     * Sends the encoded license to the device
     */
    abstract sendLicenseToDevice(encodedLicensePayload: string): Promise<void>;

    abstract sendPackageFileToDevice(encodedPackageFilePayload: PackageFileInfo): Promise<void>;

    abstract getFavoriteMeasurements(): Promise<ICamIV64File[]>;

    //#endregion
}
